import { msg } from '@/shared/i18n/msg';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatDate } from '@/shared/lib/dates/format-date';
import { formatRelative } from '@/shared/lib/dates/format-relative';
import type { Due, DueKind } from './due';
import { UrgencyPill } from './urgency-pill';

type DueBadgeProps = {
  readonly due: Due | null;
};

// Full sentences per kind: word order differs between languages (docs/conventions/i18n.md).
const UPCOMING: Readonly<Record<DueKind, string>> = {
  expires: msg('Expires {when}'),
  best_before: msg('Best before {when}'),
  replace: msg('Replace {when}'),
  shelf_life: msg('Shelf life ends {when}'),
  task: msg('Due {when}'),
  needed: msg('Needed {when}'),
};
const OVERDUE: Readonly<Record<DueKind, string>> = {
  expires: msg('Expired · {when}'),
  best_before: msg('Replace (best before) · {when}'),
  replace: msg('Replace now · {when}'),
  shelf_life: msg('Shelf life over · {when}'),
  task: msg('Overdue · {when}'),
  needed: msg('Was needed {when}'),
};
// Kinds that say something different when the date is close.
const SOON: Partial<Record<DueKind, string>> = {
  best_before: msg('Eat soon · {when}'),
};

/** Amber or red only when it calls for action; otherwise the date as plain grey text. */
export function DueBadge({ due }: DueBadgeProps) {
  const { t } = useTranslation();
  if (!due) return null;
  if (due.urgency === 'waiting' && due.startsOn) {
    return (
      <UrgencyPill urgency="waiting">{t('From {date}', { date: formatDate(due.startsOn) })}</UrgencyPill>
    );
  }
  const when = formatRelative(due.date);
  const key =
    due.days < 0
      ? OVERDUE[due.kind]
      : ((due.urgency === 'soon' ? SOON[due.kind] : undefined) ?? UPCOMING[due.kind]);
  return (
    <UrgencyPill urgency={due.urgency}>
      <span title={formatDate(due.date)}>{t(key, { when })}</span>
    </UrgencyPill>
  );
}
